import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Button,
  HStack,
  VStack,
  IconButton,
  Text,
  Link,
} from '@chakra-ui/react';
import {
  FaChevronUp,
  FaLinkedin,
  FaGithub,
  FaEnvelope,
  FaFileAlt,
} from 'react-icons/fa';

const Contact = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Box
      as="section"
      id="contact"
      bgGradient="linear(to-b, #2d4a52, #1e3238, #0f1d21)"
      py={20}
      position="relative"
      minH="60vh"
    >
      {/* Wave Transition */}
      <Box
        position="absolute"
        top="-100px"
        left="0"
        right="0"
        height="120px"
        zIndex={1}
      >
        <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          style={{ width: '100%', height: '100%' }}
        >
          <path
            d="M0,30C50,10 100,0 150,5C200,10 250,25 300,35C400,50 500,40 600,30C700,20 800,35 900,45C1000,55 1100,40 1200,25L1200,120L0,120Z"
            fill="#87CEEB"
          />
          <path
            d="M0,45C80,25 160,15 240,20C320,25 400,40 480,50C560,60 640,50 720,40C800,30 880,45 960,55C1040,65 1120,50 1200,35L1200,120L0,120Z"
            fill="#6BB6FF"
            opacity="0.8"
          />
          <path
            d="M0,60C60,40 120,30 180,35C240,40 300,55 360,65C420,75 480,65 540,55C600,45 660,60 720,70C780,80 840,65 900,55C960,45 1020,60 1080,70C1140,80 1180,70 1200,65L1200,120L0,120Z"
            fill="#4A90E2"
            opacity="0.6"
          />
        </svg>
      </Box>
      <Container maxW="1200px">
        <VStack spacing={8} textAlign="center">
          {/* Contact Title */}
          <Heading
            as="h2"
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="800"
            color="white"
            mb={4}
          >
            Let's work together
          </Heading>

          {/* Contact Buttons */}
          <HStack spacing={6} flexWrap="wrap" justify="center">
            <Button
              as="a"
              href="mailto:ramil.sharapov1988@gmail.com"
              size="lg"
              fontSize="xl"
              px={10}
              py={7}
              borderRadius="full"
              bg="transparent"
              color="white"
              border="3px solid white"
              leftIcon={<FaEnvelope />}
              _hover={{
                bg: 'white',
                color: 'brand.teal',
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              }}
              transition="all 0.3s"
            >
              Email
            </Button>
            <Button
              as="a"
              href="/RamilSharapov_CV.pdf"
              target="_blank"
              size="lg"
              fontSize="xl"
              px={10}
              py={7}
              borderRadius="full"
              bg="transparent"
              color="white"
              border="3px solid white"
              leftIcon={<FaFileAlt />}
              _hover={{
                bg: 'white',
                color: 'brand.teal',
                transform: 'translateY(-5px)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              }}
              transition="all 0.3s"
            >
              Resume
            </Button>
          </HStack>

          {/* Scroll to Top Button */}
          {showScrollTop && (
            <IconButton
              icon={
                <VStack spacing={-2}>
                  <FaChevronUp size="16px" />
                  <FaChevronUp size="16px" />
                </VStack>
              }
              onClick={scrollToTop}
              position="fixed"
              bottom={8}
              right={8}
              size="lg"
              rounded="full"
              bg="white"
              color="brand.teal"
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.2)"
              _hover={{
                bg: 'orange.400',
                color: 'white',
                transform: 'translateY(-5px)',
              }}
              transition="all 0.3s"
              aria-label="Scroll to top"
              zIndex={1000}
            />
          )}
        </VStack>
      </Container>

      {/* Footer */}
      <Box
        as="footer"
        mt={20}
        pt={12}
        position="relative"
      >
        {/* Wave Border instead of straight line */}
        <Box
          position="absolute"
          top="-5px"
          left="0"
          right="0"
          height="30px"
          overflow="hidden"
        >
          <svg
            viewBox="0 0 1200 30"
            preserveAspectRatio="none"
            style={{ width: '100%', height: '100%' }}
          >
            <path
              d="M0,15C100,5 200,25 300,15C400,5 500,25 600,15C700,5 800,25 900,15C1000,5 1100,25 1200,15L1200,30L0,30Z"
              fill="rgba(255,255,255,0.3)"
            />
            <path
              d="M0,10C150,20 300,0 450,10C600,20 750,0 900,10C1050,20 1200,0 1200,10L1200,30L0,30Z"
              fill="rgba(255,255,255,0.1)"
            />
          </svg>
        </Box>
        <Container maxW="1200px">
          <VStack spacing={4}>
            <Text fontSize="lg" color="white" fontWeight="500">
              &copy; 2025 - Ramil Sharapov
            </Text>
            <HStack spacing={6}>
              <Link
                href="https://www.linkedin.com/in/ramil-sharapov/"
                target="_blank"
                _hover={{ transform: 'scale(1.2)' }}
                transition="all 0.3s"
              >
                <IconButton
                  icon={<FaLinkedin />}
                  aria-label="LinkedIn"
                  size="lg"
                  rounded="full"
                  bg="white"
                  color="brand.teal"
                  _hover={{
                    bg: 'orange.400',
                    color: 'white',
                  }}
                />
              </Link>
              <Link
                href="https://github.com/Ramil1988"
                target="_blank"
                _hover={{ transform: 'scale(1.2)' }}
                transition="all 0.3s"
              >
                <IconButton
                  icon={<FaGithub />}
                  aria-label="GitHub"
                  size="lg"
                  rounded="full"
                  bg="white"
                  color="brand.teal"
                  _hover={{
                    bg: 'orange.400',
                    color: 'white',
                  }}
                />
              </Link>
            </HStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
